const moment = require("moment");

function formatCurrency(value, locale = "et-EE") {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function parseLocalizedNumber(str, locale = "et-EE") {
  const example = Intl.NumberFormat(locale).format(1.1);
  const decimalSeparator = example.charAt(1); // e.g., "." or ","

  // Remove everything except digits and decimal separator
  if (!str) return 0;
  if (typeof str === "number") return str;
  str = str.replace(".", ",");
  let cleaned = str
    .replace(new RegExp(`[^0-9${decimalSeparator}-]`, "g"), "")
    .replace(/[−]/g, "-")
    .replace(/(?!^)-/g, "");

  // Replace locale decimal with "."
  if (decimalSeparator !== ".") {
    cleaned = cleaned.replace(decimalSeparator, ".");
  }

  return parseFloat(cleaned);
}

function round(number, decimals) {
  return Math.round(number * 10 ** decimals) / 10 ** decimals;
}

function tokenize(str) {
  return (str ?? "")
    .split(/(\n|\s+)/) // split on newlines and whitespace, capturing them
    .filter((tok) => {
      if (tok === "\n") return true; // keep newlines
      if (/^[ \t\r]+$/.test(tok)) return false; // drop spaces & tabs
      return tok.length > 0; // keep words/symbols
    });
}

function diffWords(oldStr, newStr) {
  const a = tokenize(oldStr);
  const b = tokenize(newStr);
  const m = a.length,
    n = b.length;

  const ops = [];
  let i = 0,
    j = 0;

  while (i < m && j < n) {
    if (a[i] === b[j]) {
      ops.push({ type: "equal", text: a[i++] });
      j++;
      continue;
    }

    // Build first-appearance map for b[j:]
    const firstPosInB = new Map();
    for (let jj = j; jj < n; jj++) {
      if (!firstPosInB.has(b[jj])) firstPosInB.set(b[jj], jj);
    }

    // Find first future match between a[i:] and b[j:]
    let k = 0,
      matchJ = -1;
    for (; i + k < m; k++) {
      const tok = a[i + k];
      if (firstPosInB.has(tok)) {
        matchJ = firstPosInB.get(tok);
        break;
      }
    }

    // No future match: deletions first, then additions
    if (matchJ === -1) {
      while (i < m) ops.push({ type: "removed", text: a[i++] });
      while (j < n) ops.push({ type: "added", text: b[j++] });
      break;
    }

    // Removals up to the match in a
    while (k-- > 0) ops.push({ type: "removed", text: a[i++] });
    // Additions up to the same match in b
    while (j < matchJ) ops.push({ type: "added", text: b[j++] });

    // The matching token
    ops.push({ type: "equal", text: a[i] });
    i++;
    j++;
  }

  // Leftovers
  while (i < m) ops.push({ type: "removed", text: a[i++] });
  while (j < n) ops.push({ type: "added", text: b[j++] });

  // Merge adjacent ops of same type before returning
  return mergeAdjacentOps(ops);
}

function mergeAdjacentOps(ops) {
  const merged = [];
  for (const op of ops) {
    const last = merged[merged.length - 1];
    if (last && last.type === op.type) {
      // Concatenate token text directly to preserve whatever tokenize() emitted
      last.text += " " + op.text;
    } else {
      // Clone to avoid accidental external mutation
      merged.push({ type: op.type, text: op.text });
    }
  }
  return merged;
}

/** Utility formatting helpers */
const fmtDate = (value, pattern = "DD.MM.YYYY") => {
  return value ? moment(value, "YYYY-MM-DD").format(pattern) : null;
};

const fmtDateTime = (value) =>
  value
    ? moment(value, "YYYY-MM-DDTHH:mm:ssZ").format("DD.MM.YYYY HH:mm:ss")
    : null;
const fmtDateTimeLocalFromUTC = (value) =>
  value
    ? moment
        .utc(value, "YYYY-MM-DDTHH:mm:ssZ")
        .local()
        .format("DD.MM.YYYY HH:mm:ss")
    : null;

const fmtDateTimeLocalFromUTCTz = (value) =>
  value
    ? moment
        .utc(value, "YYYY-MM-DDTHH:mm:ssZ")
        .local()
        .format("DD.MM.YYYY HH:mm:ss Z")
    : null;

const fmtLocal = (value, fmt = "DD.MM.YYYY") => {
  // If value includes 'Z' or a timezone offset, it's UTC/offset-aware
  const isUTC = typeof value === "string" && /[Z]|[+-]\d\d:?\d\d$/.test(value);

  const m = isUTC
    ? moment.utc(value, moment.ISO_8601).local()
    : moment(value, moment.ISO_8601);

  return m.format(fmt);
};
const fmtUtc = (value, fmt = "DD.MM.YYYY") => {
  // If value includes 'Z' or a timezone offset, it's UTC/offset-aware
  const isUTC = typeof value === "string" && /[Z]|[+-]\d\d:?\d\d$/.test(value);

  const m = isUTC
    ? moment.utc(value, moment.ISO_8601)
    : moment(value, moment.ISO_8601).utc();

  return m.format(fmt);
};

function moveToSecond(arr, predicate) {
  const i = arr.findIndex(predicate);
  if (i < 0) return arr; // not found
  const [item] = arr.splice(i, 1); // remove it
  arr.splice(1, 0, item); // insert at index 1
  return arr;
}

function moveToFirst(arr, predicate) {
  const i = arr.findIndex(predicate);
  if (i < 0) return arr; // not found
  const [item] = arr.splice(i, 1); // remove it
  arr.splice(0, 0, item); // insert at index 1
  return arr;
}

function moveToIndex(arr, index, predicate) {
  const i = arr.findIndex(predicate);
  if (i < 0) return arr; // not found
  const [item] = arr.splice(i, 1); // remove it
  arr.splice(index, 0, item); // insert at index 1
  return arr;
}

const rowType = {
  approvedBudget: "approved_budget",
  selfContribution: "self_contribution",
  total: "total",
  staffCost: "staff_cost",
  revenue: "revenue",
};

/* function progressColor(value) {
  const clamped = Math.max(0, Math.min(100, Number(value)));
  const hue = (clamped / 100) * 120; // 0=red, 60=yellow, 120=green

  return `hsl(${hue}, 100%, 40%)`;
} */
function progressColor(value) {
  const v = Math.max(0, Math.min(100, Number(value)));

  const start = [0, 31, 76]; // #001f4c
  const middle = [233, 249, 247]; // #d9f9f7
  const end = [111, 222, 185]; // #6fdeb9

  let c1, c2, t;

  if (v <= 50) {
    c1 = start;
    c2 = middle;
    t = v / 50;
  } else {
    c1 = middle;
    c2 = end;
    t = (v - 50) / 50;
  }

  const r = Math.round(c1[0] + (c2[0] - c1[0]) * t);
  const g = Math.round(c1[1] + (c2[1] - c1[1]) * t);
  const b = Math.round(c1[2] + (c2[2] - c1[2]) * t);

  return `rgb(${r}, ${g}, ${b})`;
}
const allowedTypes = new Set([
  "application/pdf", // PDF
  "application/msword", // DOC
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
  "application/vnd.ms-excel", // XLS
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
  "text/csv", // CSV
  "image/jpeg", // JPG
  "image/png", // PNG
  "text/plain", // TXT
  "application/vnd.etsi.asic-e+zip", // ASiC-E (asice)
  "application/vnd.etsi.asic-s+zip", // BDOC (bdoc)
  "application/vnd.ms-powerpoint", // PPT
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // PPTX
  "application/zip", // ZIP
]);

const allowedExtensions = new Set([
  ".pdf", // PDF
  ".doc", // DOC
  ".docx", // DOCX
  ".xls", // XLS
  ".xlsx", // XLSX
  ".csv", // CSV
  ".jpg", // JPG
  ".jpeg", // JPG
  ".png", // PNG
  ".txt", // TXT
  ".asice", // ASiC-E (asice)
  ".bdoc", // BDOC (bdoc)
  ".ppt", // PPT
  ".pptx", // PPTX
  ".zip", // ZIP
]);

const options = ["Application", "Contract", "Budget file"];

const statuses = {
  DRAFT: 10,
  IDEA_CHANGES_REQUESTED: 15,
  IN_IDEA_REVIEW: 20,
  IN_PROPOSAL_DEVELOPMENT: 25,
  PROPOSAL_CHANGES_REQUESTED: 30,
  IN_APPLICATION_REVIEW: 35,
  APPLICATION_CONFIRMED: 40,
  APPLICATION_SUBMITTED: 45,
  PREPARING_FOR_SIGNOFF: 50,
  WAITING_FOR_UNIT_MANAGER_SIGNOFF: 55,
  WAITING_FOR_ACCOUNTING_SIGNOFF: 60,
  CONFIRMED: 65,
  IN_PROGRESS: 70,
  IDEA_REJECTED: 90,
  FUNDING_REJECTED: 95,
  FINISHED: 100,
};

module.exports = {
  formatCurrency,
  parseLocalizedNumber,
  round,
  diffWords,
  fmtLocal,
  fmtUtc,
  fmtDate,
  fmtDateTime,
  fmtDateTimeLocalFromUTC,
  moveToSecond,
  moveToFirst,
  moveToIndex,
  rowType,
  fmtDateTimeLocalFromUTCTz,
  progressColor,
  allowedExtensions,
  allowedTypes,
  options,
  statuses,
};
